import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

function BarChartIcon() {
    return (
        <svg
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
            className="size-4.5 shrink-0"
        >
            <rect x="1" y="10" width="3.5" height="7" rx="1" fill="currentColor" opacity="0.6" />
            <rect x="7.25" y="2" width="3.5" height="15" rx="1" fill="currentColor" />
            <rect x="13.5" y="6" width="3.5" height="11" rx="1" fill="currentColor" opacity="0.8" />
        </svg>
    );
}

function PersonIcon() {
    return (
        <svg
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
            className="size-2 shrink-0"
        >
            <circle cx="6" cy="3.5" r="2.5" fill="currentColor" />
            <path
                d="M1 10.5C1 8.015 3.239 6 6 6s5 2.015 5 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        </svg>
    );
}

type DashboardEntryButtonProps = {
    onClick: () => void;
    className?: string;
    registerTourTarget?: (id: string, el: HTMLElement | null) => void;
};

/**
 * Compact pill: chart + manager badge at rest; label expands on hover/focus.
 * Named group (`group/dash`) so sidebar's parent `group` does not expand the label.
 * SVGs must include a `size-*` class — Button forces `size-4` on unmarked SVGs,
 * which used to inflate the person badge over the chart.
 */
export function DashboardEntryButton({
    onClick,
    className,
    registerTourTarget,
}: DashboardEntryButtonProps) {
    const buttonRef = React.useCallback(
        (el: HTMLButtonElement | null) => {
            registerTourTarget?.("dashboard", el);
        },
        [registerTourTarget],
    );

    return (
        <Button
            ref={buttonRef}
            type="button"
            variant="ghost"
            size="xs"
            onClick={onClick}
            title="Дашборд руководителя"
            aria-label="Дашборд руководителя"
            data-tour-target="dashboard"
            className={cn(
                'group/dash relative z-10 flex shrink-0 items-center gap-0 overflow-hidden whitespace-nowrap',
                'rounded-s border border-neutral-border-300 bg-neutral-background-100/60',
                'px-2 py-1.5 text-caption font-medium text-neutral-text-500',
                'transition-all duration-200 ease-in-out motion-reduce:transition-none',
                'hover:gap-2 hover:border-primary-500/55 hover:bg-gradient-to-br hover:from-primary-500/25 hover:to-state-info/12',
                'hover:pr-3 hover:text-primary-500 hover:ring-[3px] hover:ring-primary-500/15',
                'focus-visible:gap-2 focus-visible:border-primary-500/55 focus-visible:pr-3 focus-visible:text-primary-500',
                'focus-visible:bg-gradient-to-br focus-visible:from-primary-500/25 focus-visible:to-state-info/12',
                'focus-visible:ring-[3px] focus-visible:ring-primary-500/15 focus-visible:outline-none',
                className,
            )}
        >
            <span className="relative flex shrink-0 items-center justify-center">
                <BarChartIcon />
                <span
                    className={cn(
                        'absolute -right-0.5 -bottom-0.5 flex rounded-full bg-neutral-text-500 p-px text-neutral-white',
                        'transition-colors duration-200 ease-in-out motion-reduce:transition-none',
                        'group-hover/dash:bg-primary-500 group-focus-visible/dash:bg-primary-500',
                    )}
                >
                    <PersonIcon />
                </span>
            </span>
            <span
                className={cn(
                    'max-w-0 overflow-hidden text-caption font-medium tracking-wide opacity-0',
                    'transition-[max-width,opacity] duration-200 ease-in-out motion-reduce:transition-none',
                    'group-hover/dash:max-w-32 group-hover/dash:opacity-100',
                    'group-focus-visible/dash:max-w-32 group-focus-visible/dash:opacity-100',
                )}
            >
                Дашборд
            </span>
        </Button>
    );
}
